import dayjs from "dayjs"
import { Dispatch, Fragment, SetStateAction, useState } from "react"
import { Box, Button, createTheme, ThemeProvider, useTheme } from "@mui/material"
import { DataGrid, gridClasses } from "@mui/x-data-grid"
import { FormattedBiobox, OpenAssignBoxModal } from "../types"
import { HtmlTooltip } from "../../dentalenhancements/components/ToolTip"
import { getBackgroundComplianceColor } from "../../../../helpers/compliance"
import { getBioboxes, toggleBioboxActivation } from "../../../../services/biologicalServices"
import { Biobox } from "../../../../types"
import { useAppDispatch } from "../../../../redux/store"
import { getExpirationDateClass, getTestsLeftClass } from "../utils/helpers"
import { addError, addSuccess } from "../../../../redux/alerts"
import useCustomerNumber from "../../../../hooks/useCustomerNumber"

type BioboxTable = {
  isActive: boolean,
  bioboxes: FormattedBiobox[],
  setBioboxes: Dispatch<SetStateAction<Biobox[]>>,
  setOpenAssignBioboxModal: Dispatch<SetStateAction<OpenAssignBoxModal>>,
}

const BioboxTable = (props: BioboxTable) => {
  const {
    isActive,
    bioboxes,
    setBioboxes,
    setOpenAssignBioboxModal,
  } = props

  const theme = useTheme()
  const dispatch = useAppDispatch();
  const customerNumber = useCustomerNumber()

  const [updatingBox, setIsUpdatingBox] = useState<{ boxId: number, isUpdating: boolean} | null>(null)

  const tableTheme = createTheme({
    typography: {
      fontFamily: '"Open Sans", sans-serif',
      fontSize: 13
    }
  })

  const handleActiveToggle = async (biobox: FormattedBiobox) => {
    try {
      setIsUpdatingBox({ boxId: biobox.box_id, isUpdating: true })

      const clonedBox = structuredClone(biobox)
      await toggleBioboxActivation(customerNumber, biobox.box_id)
      const bioboxData: Biobox[] = await getBioboxes(customerNumber)

      setBioboxes(bioboxData)
      setIsUpdatingBox(null)

      dispatch(addSuccess('Box has been updated'))
    } catch (err: any) {
      console.error('Error Updating Biobox', err)
      setIsUpdatingBox(null)
      
      const errorMessage = err?.response?.data?.message || err?.message || 'There was a problem updating the box'
      dispatch(addError(errorMessage))
    }
  }

  const handleAssignBox = (biobox: FormattedBiobox) => {
    setOpenAssignBioboxModal({ open: true, autoclave: null, biobox })
  }

  return (
    <ThemeProvider theme={tableTheme}>
      <DataGrid
        sx={{
          borderLeft: "none",
          borderRight: "none",
          borderBottom: "none",
          borderRadius: "0",
          [`& .${gridClasses.cell}`]: {
            display: 'flex',
            alignItems: 'flex-start',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 0.5,
          },
          '& .MuiDataGrid-columnHeader--moving': {
            backgroundColor: theme.palette.common.white,
          }
        }}
        getRowId={row => row.box_id}
        rows={bioboxes}
        getRowHeight={() => 'auto'}
        sortingOrder={['asc', 'desc']}
        columns={[
          {
            width: 100,
            field: "compliance_status",
            valueGetter: (value, row, column) => row.compliance[0].compliance_status,
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                Status
              </Box>
            ),
            renderCell: (params) => (
              <HtmlTooltip
                arrow={true}
                placement="top"
                disableInteractive
                title={
                  <Fragment>
                    {
                      params.row.compliance.map((complianceStatus, index) => (
                        <Box key={`compliance-tool-tip-message-${index}`}>
                          { complianceStatus.compliance_message_short }
                        </Box>
                      ))
                    }
                  </Fragment>
                }
              >
                <Box sx={{ alignSelf: 'center' }}>
                  <span className={`compliance-circle ${getBackgroundComplianceColor(params.row.compliance[0].compliance_status)}`}/>
                </Box>
              </HtmlTooltip>
            ),
          },
          {
            minWidth: 250,
            flex: 1,
            field: "box_number",
            valueGetter: (value, row, column) => row.box_number,
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                Box Number
              </Box>
            ),
          },
          {
            minWidth: 250,
            flex: 1,
            field: "lot_number",
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                Lot Number
              </Box>
            ),
          },
          {
            minWidth: 250,
            flex: 1,
            field: "tests_left",
            valueGetter: (value, row, column) => (row.box_size - row.tests_run),
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                Tests Left
              </Box>
            ),
            renderCell: (params) => (
              <Box className="flex flex-align-center" sx={{ columnGap: '4px' }}>
                <span className={getTestsLeftClass(params.row.box_size, params.row.tests_run)}>
                  {params.row.box_size - params.row.tests_run}
                </span>
                <span className="muted-text">of {params.row.box_size}</span>
              </Box>
            ),
          },
          {
            minWidth: 250,
            flex: 1,
            field: "exp_date",
            renderHeader: () => (
              <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>
                Expires
              </Box>
            ),
            renderCell: params => {
              return (
                <span className={getExpirationDateClass(params.row.exp_date)}>
                  {dayjs(params.row.exp_date).format('M/D/YY')}
                </span>
              )
            },
            getSortComparator: (sortDirection) => {
              const modifier = sortDirection === 'desc' ? -1 : 1

              return (a, b) => {
                const date = Date.parse(a)
                const date2 = Date.parse(b)

                if (isNaN(date) && isNaN(date2)) {
                  return 0
                } else if (isNaN(date) && !isNaN(date2)) {
                  return 1
                } else if (!isNaN(date) && isNaN(date2)) {
                  return -1
                } else {
                  return (date - date2) * modifier
                }
              }
            },
          },
          {
            minWidth: 200,
            flex: 1,
            field: "assign_box_to_autoclave",
            headerName: "",
            sortable: false,
            renderCell: (params) => {
              const box = params.row
              const showButton = box.active && box.box_type === 'mesa' && box.autoclave_id === null

              return showButton && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleAssignBox(params.row)}
                  sx={{ alignSelf: 'center', textTransform: 'none', backgroundColor: theme.palette.primary.main, ':hover': { backgroundColor: '#007ba9' } }}
                >
                  Assign To Autoclave
                </Button>
              )
            },
          },
          {
            minWidth: 200,
            flex: 1,
            field: "deactivate",
            headerName: "",
            sortable: false,
            renderCell: (params) => {
              const box = params.row
              const showButton = isActive || box.compliance[0].compliance_code === 'BIO_BOX_INACTIVE'

              const isUpdating = updatingBox?.isUpdating
                && box.box_id === updatingBox.boxId

              return showButton && (
                <Button
                  size="small"
                  variant="contained"
                  disabled={Boolean(isUpdating)}
                  onClick={() => handleActiveToggle(params.row)}                
                  sx={{ alignSelf: 'center', textTransform: 'none', backgroundColor: theme.palette.primary.main, ':hover': { backgroundColor: '#007ba9' } }}
                >
                  { isActive ? 'Deactivate' : 'Activate' }
                </Button>                
              )
            },
          },
        ]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
          sorting: {
            sortModel: [
              {
                field: 'compliance_status',
                sort: 'asc'
              }
            ]
          }
        }}
        pageSizeOptions={[5, 10, 20, 25, 50, 100]}
        autoHeight
        density="compact"
      />
    </ThemeProvider>
  )
}

export default BioboxTable
